import { faPlus, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import {
    strollersAddReviewUrl,
    strollersDetailUrl,
    strollersReviewsUrl,
} from "@/mk2/apps/strollers/urls";
import { Btn, BtnSidePadding, BtnType } from "@/mk2/components/Btn";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Link, OwnProps as LinkProps } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { StarsRating } from "@/mk2/components/StarsRating";
import { url } from "@/mk2/helpers/urls";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { StrollerUserReviewStatus } from "@/mk2/schemas";
import {
    MY_PRODUCT_NO_REVIEW,
    MY_PRODUCT_REVIEW_WAITING_FOR_APPROVE,
    REMOVE_FROM_PROFILE,
    STROLLER_ADD_REVIEW,
    USER_STROLLERS_MY_REVIEW,
} from "@/mk/autogenerated/translations/MyProduct.c6ecdce1148171aa5fbc3b400f8e8d89";

import styles from "./MyProduct.mscss";

type ID = string | number;

interface OwnProps {
    id?: ID;
    className?: string;
    image: React.ReactNode;
    to?: LinkProps["to"];
    title: React.ReactNode;
    description?: React.ReactNode;
    isEditing?: boolean;
    onRemove?(id: ID, event: React.MouseEvent<HTMLAnchorElement>);
    onClick?(id: ID, event: React.MouseEvent<HTMLDivElement>);
}

type Props = OwnProps;

const MyProduct: React.FunctionComponent<Props> = ({
    id,
    className,
    to,
    image,
    title,
    description,
    isEditing = false,
    onRemove,
    onClick,
}) => {
    const onClickCallback = useEventCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            onClick?.(id, event);
        },
        [onClick, id],
    );

    const onRemoveCallback = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            onRemove?.(id, event);
        },
        [onRemove, id],
    );

    return (
        <div className={cx(styles.MyProduct, className)} onClick={onClickCallback}>
            {to ? (
                <Link to={to} className={styles.MyProduct__photo}>
                    {image}
                </Link>
            ) : (
                <span className={styles.MyProduct__photo}>{image}</span>
            )}
            <div className={styles.MyProduct__info}>
                {to ? (
                    <Link className={styles.MyProduct__info__title} to={to}>
                        {title}
                    </Link>
                ) : (
                    <span className={styles.MyProduct__info__title}>{title}</span>
                )}
                <div className={styles.MyProduct__info__description}>
                    {isEditing ? (
                        <Btn
                            label={REMOVE_FROM_PROFILE}
                            type={BtnType.RedText}
                            icon={faTimes}
                            sidePadding={BtnSidePadding.None}
                            onClick={onRemoveCallback}
                        />
                    ) : (
                        description
                    )}
                </div>
            </div>
        </div>
    );
};

MyProduct.displayName = "MyProduct";

export default React.memo<Props>(MyProduct);

interface MyProductPlaceholderProps {
    className?: string;
}

export const MyProductPlaceholder: React.FunctionComponent<MyProductPlaceholderProps> = ({
    className,
}) => (
    <MyProduct
        className={className}
        image={<LoadingPlaceholder width={80} height={80} />}
        title={<LoadingPlaceholder width={150} />}
    />
);

MyProductPlaceholder.displayName = "MyProductPlaceholder";

interface MyStrollerProductProps {
    id?: ID;
    className?: string;
    slug: string;
    name: string;
    imageUrl: string;
    imageWidth: number;
    imageHeight: number;
    reviewId?: number;
    reviewStatus?: StrollerUserReviewStatus;
    reviewerScore?: number;
    isEditing?: boolean;
    canReview?: boolean;
    pathname?: string;
    onRemove?(id: ID, event: React.MouseEvent<HTMLAnchorElement>);
    onClick?(id: ID, event: React.MouseEvent<HTMLDivElement>);
}

export const MyStrollerProduct: React.FunctionComponent<MyStrollerProductProps> = ({
    id,
    className,
    slug,
    name,
    imageUrl,
    imageHeight,
    imageWidth,
    reviewId,
    reviewStatus,
    reviewerScore,
    isEditing,
    onRemove,
    onClick,
    canReview,
    pathname,
}) => (
    <MyProduct
        id={id}
        className={cx(styles.MyStrollerProduct, className)}
        isEditing={isEditing}
        onClick={onClick}
        onRemove={onRemove}
        to={url(strollersDetailUrl, { slug })}
        title={name}
        image={
            <ImgCropped
                className={styles.MyProduct__photo__img}
                width={80}
                height={80}
                mode={ImgCropMode.Embed}
                imgWidth={imageWidth}
                imgHeight={imageHeight}
                imgUrl={imageUrl}
            />
        }
        description={
            reviewId &&
            (reviewStatus === StrollerUserReviewStatus.NOT_VERIFIED_AND_APPROVED ||
                reviewStatus === StrollerUserReviewStatus.VERIFIED_AND_APPROVED) ? (
                // User added review and is approved
                <Link
                    className={styles.MyStrollerProduct__review}
                    to={url(strollersReviewsUrl, { slug }, {}, `review-${reviewId}`)}
                >
                    {reviewerScore > 0 && (
                        <span className={styles.MyStrollerProduct__review__score}>
                            <StarsRating starsCount={reviewerScore} canShowHalf />
                        </span>
                    )}
                    <span className={styles.MyStrollerProduct__review__text}>
                        {USER_STROLLERS_MY_REVIEW}
                    </span>
                </Link>
            ) : canReview && !reviewId ? (
                // User has not added review but he can
                <Link
                    className={styles.MyStrollerProduct__review}
                    to={url(strollersAddReviewUrl, { slug }, { next: pathname })}
                >
                    {reviewerScore > 0 && (
                        <span className={styles.MyStrollerProduct__review__score}>
                            <StarsRating starsCount={reviewerScore} canShowHalf />
                        </span>
                    )}
                    <span className={styles.MyStrollerProduct__review__text}>
                        {STROLLER_ADD_REVIEW}
                    </span>
                </Link>
            ) : (
                // User has not added review or review is not approved
                <span className={styles.MyStrollerProduct__review}>
                    {reviewerScore > 0 && (
                        <span className={styles.MyStrollerProduct__review__score}>
                            <StarsRating starsCount={reviewerScore} canShowHalf />
                        </span>
                    )}
                    {reviewId &&
                    (reviewStatus === StrollerUserReviewStatus.NOT_APPROVED_YET ||
                        reviewStatus === StrollerUserReviewStatus.NOT_APPROVED_WANT_VERIFY) ? (
                        <span
                            className={cx(
                                styles.MyStrollerProduct__review__text,
                                styles["MyStrollerProduct__review__text--notApproved"],
                            )}
                        >
                            {MY_PRODUCT_REVIEW_WAITING_FOR_APPROVE}
                        </span>
                    ) : (
                        <span
                            className={cx(
                                styles.MyStrollerProduct__review__text,
                                styles["MyStrollerProduct__review__text--noReview"],
                            )}
                        >
                            {MY_PRODUCT_NO_REVIEW}
                        </span>
                    )}
                </span>
            )
        }
    />
);

MyStrollerProduct.displayName = "MyStrollerProduct";

interface MyProductAddProps {
    className?: string;
    label: React.ReactNode;
    onClick?(id: ID, event: React.MouseEvent<HTMLDivElement>);
}

export const MyProductAdd: React.FunctionComponent<MyProductAddProps> = ({
    className,
    label,
    onClick,
}) => (
    <MyProduct
        className={cx(styles.MyProductAdd, className)}
        title={label}
        onClick={onClick}
        image={
            <div className={styles.MyProductAdd__image}>
                <FontAwesomeIcon icon={faPlus} />
            </div>
        }
    />
);

MyProductAdd.displayName = "MyProductAdd";
